/**
 * Lista de Agentes IA (F1.1+).
 *
 * Página enxuta — apenas listagem + atalhos. Criação/edição vai para a
 * página dedicada `/agents/new` (ou `/agents/:id`). Sem mais modais grandes.
 */

import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Bot, Plus, Edit3, Copy } from "lucide-react";
import { AGENT_TEMPLATES } from "./agents/templates";

export default function Agents() {
  const [, navigate] = useLocation();
  const { data: agents = [], isLoading } = trpc.agents.list.useQuery();
  const { data: credentials = [] } = trpc.llmCredentials.list.useQuery();

  const noCredentials = credentials.length === 0;

  return (
    <PermissionGuard permission="ai:agents_manage">
      <DashboardLayout>
        <div className="flex flex-col gap-6 p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Bot className="w-6 h-6 text-violet-600" />
                Agentes IA
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Crie agentes especializados (Vendas, Suporte, Onboarding…) com
                prompt + base de conhecimento + ferramentas próprias.
              </p>
            </div>
            <Button onClick={() => navigate("/agents/new")} className="gap-2">
              <Plus className="w-4 h-4" />
              Novo agente em branco
            </Button>
          </div>

          {noCredentials && (
            <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20">
              <CardContent className="pt-3 pb-3 text-sm">
                <strong>Antes de criar agentes:</strong> cadastre pelo menos uma{" "}
                <Link href="/settings?tab=integrations&integration=llm" className="underline">
                  chave de API (BYOK)
                </Link>{" "}
                do provider que você usa (OpenAI, Anthropic ou Google). Sem chave,
                o agente fica inativo.
              </CardContent>
            </Card>
          )}

          {/* Templates clonáveis */}
          <div>
            <h2 className="text-sm font-medium text-muted-foreground mb-3">
              Começar a partir de um template
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {AGENT_TEMPLATES.map((t) => (
                <Card
                  key={t.key}
                  className="border-border/50 hover:border-primary/40 cursor-pointer transition-colors"
                  onClick={() => navigate(`/agents/new?template=${t.key}`)}
                >
                  <CardContent className="pt-4 pb-4 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{t.name}</span>
                      <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t.shortDescription}
                    </p>
                    <p className="text-xs font-mono text-muted-foreground/70">
                      {t.suggestedModel} · {t.allowedTools.length} tools
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Lista de agentes existentes */}
          <div>
            <h2 className="text-sm font-medium text-muted-foreground mb-3">
              Seus agentes
            </h2>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Carregando…</div>
            ) : agents.length === 0 ? (
              <Card className="border-dashed border-2 border-border/50">
                <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
                  <Bot className="w-12 h-12 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">
                    Nenhum agente criado ainda. Clone um template acima ou crie
                    do zero.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {agents.map((a) => (
                  <Card
                    key={a.id}
                    className="border-border/50 hover:border-primary/30 cursor-pointer transition-colors"
                    onClick={() => navigate(`/agents/${a.id}`)}
                  >
                    <CardContent className="pt-4 pb-4 flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate">{a.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {a.key}
                            </Badge>
                            {!a.isActive && (
                              <Badge
                                variant="outline"
                                className="text-xs text-muted-foreground"
                              >
                                inativo
                              </Badge>
                            )}
                            {!a.credentialId && (
                              <Badge
                                variant="outline"
                                className="text-xs text-amber-600 border-amber-300"
                              >
                                sem credencial
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 font-mono">
                            {a.model} · temp {Number(a.temperature).toFixed(2)} ·{" "}
                            {a.allowedTools?.length ?? 0} tools
                          </p>
                          {a.currentVersion && (
                            <p className="text-xs text-muted-foreground mt-1">
                              versão atual: v{a.currentVersion.version}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/agents/${a.id}`);
                          }}
                          className="shrink-0"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </DashboardLayout>
    </PermissionGuard>
  );
}
